<template>
  <aside
    class="ai-chat app-card"
    :class="{ 'ai-chat--wide': wide }"
    :aria-label="comparing ? `Ask across ${catalogs.length} catalogs` : 'Ask this catalog'"
  >
    <div class="ai-chat__inner">
    <header class="ai-chat__header">
      <div class="flex items-center justify-between gap-2">
        <h2 class="ds-heading ai-chat__title">
          {{ comparing ? `Ask across ${catalogs.length} catalogs` : "Ask this catalog" }}
          <span class="ds-pill ds-pill--outline ai-chat__beta">Prototype</span>
        </h2>
        <div class="flex items-center gap-1">
        <button
          v-if="turns.length"
          type="button"
          class="ds-button ds-button--ghost ai-chat__new"
          :disabled="busy"
          title="Start over: the next question won't refer back to earlier ones"
          @click="turns = []"
        >
          New conversation
        </button>
        <button
          type="button"
          class="ds-button ds-button--ghost ai-chat__icon-btn"
          :aria-label="wide ? 'Narrow panel' : 'Widen panel'"
          :title="wide ? 'Narrow panel' : 'Widen panel'"
          @click="wide = !wide"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path v-if="wide" d="M14 6l6 6-6 6M4 12h16" />
            <path v-else d="M10 6l-6 6 6 6M4 12h16" />
          </svg>
        </button>
        </div>
      </div>
      <p class="ai-chat__status" :class="`ai-chat__status--${overall.state}`" role="status">
        {{ overall.text }}
      </p>
    </header>

    <div ref="thread" class="ai-chat__thread">
      <div v-if="!turns.length" class="ai-chat__empty">
        <p class="text-sm text-mute mb-3">
          <template v-if="comparing">
            Ask once and each catalog answers on its own, then the answers are
            lined up side by side. Every point cites its catalog's page; click
            one to open that catalog there.
          </template>
          <template v-else>
            Ask a question in plain English. Answers come only from this catalog
            and cite the pages they used; click a page to jump there. Follow-up
            questions ("what are its prerequisites?") refer back to earlier ones.
          </template>
        </p>
        <span class="ds-field-label">Try one of these</span>
        <ul class="ai-chat__suggestions">
          <li v-for="q in suggestions" :key="q">
            <button type="button" class="ds-link text-left text-sm" :disabled="unusable" @click="submit(q)">{{ q }}</button>
          </li>
        </ul>
      </div>

      <div v-for="turn in turns" :key="turn.id" class="ai-chat__turn">
        <p class="ai-chat__question">{{ turn.question }}</p>

        <CompareAnswer
          v-if="turn.kind === 'compare'"
          :turn="turn"
          :catalogs="turn.catalogs"
          @goto="$emit('goto-page', $event)"
        />

        <div v-else class="ai-chat__reply">
          <div v-if="turn.reply.loading" class="ai-chat__loading text-sm text-mute">
            <span class="ai-chat__dot"></span><span class="ai-chat__dot"></span><span class="ai-chat__dot"></span>
          </div>
          <p v-else-if="turn.reply.error" class="ds-error text-sm">{{ turn.reply.error }}</p>
          <template v-else>
            <!-- renderAnswer() escapes everything before adding its own tags -->
            <div class="ai-chat__answer" v-html="renderAnswer(turn.reply.answer)"></div>
            <div v-if="turn.reply.pages.length" class="ai-chat__pages">
              <button
                v-for="p in turn.reply.pages"
                :key="p"
                type="button"
                class="ds-pill ds-pill--outline ai-chat__page"
                @click="$emit('goto-page', { catalogId: turn.catalogs[0].id, page: p })"
              >
                p. {{ p }}
              </button>
            </div>
            <div class="ai-chat__meta">
              {{ formatCost(turn.reply.costUsd) }} · {{ (turn.reply.latencyMs / 1000).toFixed(1) }}s ·
              {{ turn.reply.usage.inputTokens.toLocaleString() }} in / {{ turn.reply.usage.outputTokens.toLocaleString() }} out
            </div>
          </template>
        </div>
      </div>
    </div>

    <form class="ai-chat__form" @submit.prevent="submit()">
      <span class="ds-input-wrap ai-chat__input-wrap">
        <textarea
          v-model="draft"
          rows="2"
          maxlength="500"
          :placeholder="listening ? 'Listening…' : placeholder"
          @keydown.enter.exact.prevent="submit()"
        ></textarea>
      </span>
      <div class="flex gap-2">
        <button
          v-if="speechSupported"
          type="button"
          class="ds-button ds-button--secondary ai-chat__icon-btn"
          :class="{ 'ai-chat__mic--on': listening }"
          :aria-label="listening ? 'Stop listening' : 'Ask by voice'"
          :title="listening ? 'Stop listening' : 'Ask by voice'"
          @click="toggleVoice"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="9" y="3" width="6" height="11" rx="3" />
            <path d="M5 11a7 7 0 0014 0M12 18v3" />
          </svg>
        </button>
        <button type="submit" class="ds-button ds-button--primary" style="flex: 1" :disabled="!draft.trim() || busy || unusable">
          {{ !comparing ? "Ask" : catalogs.length === 2 ? "Ask both" : `Ask all ${catalogs.length}` }}
        </button>
      </div>
    </form>
    </div>
  </aside>
</template>

<script>
// AI chat proof of concept, shown by App.vue on staging for any PDF catalog —
// one catalog, or 2–3 side by side (CompareShelf).
//
// Catalogs are read live: the browser downloads each PDF straight from S3,
// extracts its text and builds a search index in memory (src/ai/catalogStore).
// Nothing is stored anywhere. For each question the browser picks each
// catalog's relevant pages (selectPages) and sends only those — to /api/ask
// for one catalog, or /api/compare (streamed) for several.
import questions from "../../scripts/ai-poc/questions.json";
import { selectPages } from "../../lib/ai-poc/answer.mjs";
import { readCatalog, readStatus, getIndex, touch } from "@/ai/catalogStore";
import { renderAnswer } from "@/ai/renderAnswer";
import { memberstack } from "@/auth/client";
import CompareAnswer from "./ai/CompareAnswer.vue";

const GENERIC_SUGGESTIONS = [
  "What is this college's policy on credit for prior learning?",
  "Does the college recognize ACE guidelines for military credit?",
  "What degrees does this college offer?",
  "What is the tuition or per-unit fee?",
  "Show me the course description for English composition.",
];
const COMPARE_SUGGESTIONS = [
  "How do these colleges award credit for military service?",
  "How do the nursing degree requirements differ?",
  "Compare tuition and fees.",
  "How do they award credit for AP and CLEP exams?",
];
// Two editions of the same college.
const YEAR_SUGGESTIONS = [
  "How do the associate degree general education requirements compare?",
  "Compare tuition and fees.",
  "How do the nursing program requirements compare?",
];

const SpeechRecognition =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

// The Memberstack session token lives in localStorage, so the browser won't
// send it on its own; the endpoints verify it server-side.
function authHeaders() {
  const token = memberstack.getMemberCookie();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default {
  name: "AiChatPanel",
  components: { CompareAnswer },
  props: {
    // [{ id, pdfUrl, college, edition, tone }] — one catalog, or 2–3 to compare.
    catalogs: { type: Array, required: true },
  },
  emits: ["goto-page"],
  data() {
    return {
      draft: "",
      turns: [],
      wide: this.catalogs.length > 1,
      listening: false,
      speechSupported: !!SpeechRecognition,
      recognition: null,
    };
  },
  computed: {
    comparing() {
      return this.catalogs.length > 1;
    },
    setKey() {
      return this.catalogs.map((c) => c.id).join(",");
    },
    statuses() {
      return this.catalogs.map((c) => readStatus(c.id));
    },
    overall() {
      const st = this.statuses;
      const bad = st.findIndex((s) => s.state === "too-large" || s.state === "error");
      if (bad > -1) {
        const c = this.catalogs[bad];
        const why = st[bad].state === "too-large" ? "is too large for AI chat in the prototype" : "couldn't be read";
        return {
          state: "error",
          text: this.comparing ? `${c.college} ${c.edition} ${why}. Remove it to compare the others.` : `This catalog ${why}.`,
        };
      }
      if (!this.comparing) {
        const s = st[0];
        if (s.state === "ready") return { state: "ready", text: `Ready · ${s.total} pages` };
        return { state: "reading", text: s.total ? `Reading catalog… ${s.done} / ${s.total} pages` : "Loading catalog…" };
      }
      const ready = st.filter((s) => s.state === "ready").length;
      if (ready === st.length) return { state: "ready", text: `Ready · ${st.reduce((n, s) => n + s.total, 0)} pages across ${st.length} catalogs` };
      return { state: "reading", text: `Reading catalogs… ${ready} of ${st.length} ready` };
    },
    unusable() {
      return this.overall.state === "error";
    },
    busy() {
      return this.turns.some((t) => (t.kind === "compare" ? !t.done && !t.error : t.reply.loading));
    },
    sameCollege() {
      return new Set(this.catalogs.map((c) => c.college)).size === 1;
    },
    suggestions() {
      if (this.comparing) return this.sameCollege ? YEAR_SUGGESTIONS : COMPARE_SUGGESTIONS;
      return questions[String(this.catalogs[0].id)] || GENERIC_SUGGESTIONS;
    },
    placeholder() {
      return this.comparing
        ? "e.g. How do their transfer credit policies differ?"
        : "e.g. What does it take to earn the nursing degree?";
    },
  },
  watch: {
    setKey: {
      immediate: true,
      handler(now, before) {
        // A different set of catalogs is a different conversation.
        if (before !== undefined) this.turns = [];
        if (before !== undefined && this.catalogs.length > 1) this.wide = true;
        touch(this.catalogs.map((c) => c.id));
        for (const c of this.catalogs) readCatalog(c.id, c.pdfUrl, `${c.college} ${c.edition}`);
      },
    },
  },
  beforeUnmount() {
    if (this.recognition) this.recognition.abort();
  },
  methods: {
    renderAnswer,

    // An index, waiting for the catalog if it's still being read.
    indexFor(c) {
      return getIndex(c.id) || readCatalog(c.id, c.pdfUrl, `${c.college} ${c.edition}`);
    },

    submit(text) {
      const question = (text || this.draft).trim();
      if (!question || this.busy || this.unusable) return;
      this.draft = "";
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "ai_poc_question", catalogs: this.catalogs.length });
      if (this.comparing) this.askCompare(question);
      else this.askOne(question);
      this.scrollToBottom();
    },

    async askOne(question) {
      const catalog = this.catalogs[0];
      this.turns.push({ id: Date.now(), kind: "one", question, catalogs: [catalog], reply: { loading: true } });
      // Take the reactive proxy back out of the array: writing onto the plain
      // object pushed above would never re-render.
      const turn = this.turns[this.turns.length - 1];
      // Earlier answered turns give follow-ups their context.
      const history = this.turns
        .slice(0, -1)
        .filter((t) => t.kind === "one" && t.reply.status === "answered")
        .map((t) => ({ question: t.question, answer: t.reply.answer, pages: t.reply.pages }))
        .slice(-3);
      turn.reply = { loading: false, ...(await this.postOne(catalog, question, history)) };
      this.scrollToBottom();
    },

    async postOne(catalog, question, history) {
      const index = await this.indexFor(catalog);
      if (!index) return { error: "This catalog couldn't be read, so AI chat isn't available for it." };
      const { pages, programs } = selectPages(index, question, history);
      // Nothing in the catalog matches, so there's nothing to ask the model.
      if (!pages.length) return notFound();
      try {
        const res = await fetch("/api/ask", {
          method: "POST",
          headers: authHeaders(),
          credentials: "same-origin",
          body: JSON.stringify({ catalogId: String(catalog.id), question, history, pages, programs }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: data.error || `Request failed (${res.status}).` };
        return data;
      } catch (e) {
        return { error: "Couldn't reach the AI service." };
      }
    },

    async askCompare(question) {
      const catalogs = this.catalogs.map((c) => ({ ...c }));
      this.turns.push({
        id: Date.now(),
        kind: "compare",
        question,
        catalogs,
        legs: catalogs.map(() => ({ loading: true })),
        comparison: null,
        done: null,
        error: null,
      });
      const turn = this.turns[this.turns.length - 1];

      // Each catalog's follow-up context: its own earlier answers.
      const earlier = this.turns.slice(0, -1).filter((t) => t.kind === "compare" && t.done);
      const entries = [];
      for (const c of catalogs) {
        const index = await this.indexFor(c);
        if (!index) {
          turn.error = `${c.college} ${c.edition} couldn't be read, so it can't be compared.`;
          return;
        }
        const history = earlier
          .map((t) => {
            const i = t.catalogs.findIndex((x) => x.id === c.id);
            const leg = i > -1 ? t.legs[i] : null;
            return leg && leg.status === "answered" ? { question: t.question, answer: leg.answer, pages: leg.pages } : null;
          })
          .filter(Boolean)
          .slice(-3);
        const { pages, programs } = selectPages(index, question, history);
        entries.push({ catalogId: String(c.id), pages, programs, history });
      }

      try {
        const res = await fetch("/api/compare", {
          method: "POST",
          headers: authHeaders(),
          credentials: "same-origin",
          body: JSON.stringify({ question, catalogs: entries }),
        });
        if (!res.ok || !res.body) {
          const data = await res.json().catch(() => ({}));
          turn.error = data.error || `Request failed (${res.status}).`;
          return;
        }
        // Newline-delimited JSON events; see functions/api/compare.js.
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let nl;
          while ((nl = buffer.indexOf("\n")) > -1) {
            const line = buffer.slice(0, nl).trim();
            buffer = buffer.slice(nl + 1);
            if (line) this.onCompareEvent(turn, JSON.parse(line));
          }
        }
        if (!turn.done && !turn.error) turn.error = "The comparison was cut short. Try again.";
      } catch (e) {
        turn.error = "Couldn't reach the AI service.";
      }
    },

    onCompareEvent(turn, event) {
      if (event.type === "leg") turn.legs[event.index] = { loading: false, ...event.result };
      else if (event.type === "comparison") turn.comparison = event;
      else if (event.type === "done") turn.done = event;
      else if (event.type === "error") turn.error = event.error;
      this.scrollToBottom();
    },

    toggleVoice() {
      if (this.listening) {
        this.recognition.stop();
        return;
      }
      const rec = new SpeechRecognition();
      rec.lang = "en-US";
      rec.interimResults = true;
      const before = this.draft ? `${this.draft.trim()} ` : "";
      rec.onresult = (e) => {
        this.draft = before + Array.from(e.results).map((r) => r[0].transcript).join("");
      };
      rec.onend = () => {
        this.listening = false;
      };
      rec.onerror = () => {
        this.listening = false;
      };
      this.recognition = rec;
      this.listening = true;
      rec.start();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.thread;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    formatCost(usd) {
      return usd < 0.01 ? `${(usd * 100).toFixed(2)}¢` : `$${usd.toFixed(3)}`;
    },

  },
};

function notFound() {
  return {
    status: "not_found",
    answer: "Not found in this catalog.",
    pages: [],
    usage: { inputTokens: 0, outputTokens: 0 },
    costUsd: 0,
    latencyMs: 0,
  };
}
</script>

<style scoped>
/* The inner column is absolutely positioned so the panel takes the viewer
   card's height instead of growing with the conversation; only the thread
   scrolls. */
.ai-chat {
  position: relative;
  width: 400px;
  flex: none;
  overflow: hidden;
  transition: width 0.2s ease;
}
.ai-chat__inner {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
}
.ai-chat--wide {
  width: 55%;
}
.ai-chat__header {
  padding: var(--space-4) var(--space-4) var(--space-3);
  border-bottom: 1px solid var(--line);
}
.ai-chat__status {
  margin: var(--space-2) 0 0;
  font-size: 0.75rem;
  color: var(--mute);
}
.ai-chat__status--too-large,
.ai-chat__status--error {
  color: var(--error);
}
.ai-chat__title {
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.ai-chat__beta {
  font-family: var(--font-sans);
  font-size: 0.65rem;
}
.ai-chat__new {
  font-size: 0.75rem;
  padding: 0.3rem 0.5rem;
  min-width: 0;
}
.ai-chat__icon-btn {
  padding: 0.4rem;
  min-width: 0;
}
.ai-chat__icon-btn svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.ai-chat__mic--on {
  color: var(--error);
  border-color: var(--error);
}
.ai-chat__thread {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}
.ai-chat__suggestions {
  list-style: none;
  padding: 0;
  margin: var(--space-2) 0 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.ai-chat__question {
  align-self: flex-end;
  background: var(--ink);
  color: var(--paper);
  border-radius: var(--r-card-sm);
  padding: var(--space-2) var(--space-3);
  font-size: 0.9rem;
  max-width: 90%;
  margin-left: auto;
  margin-bottom: var(--space-3);
  width: fit-content;
}
.ai-chat__reply {
  font-size: 0.9rem;
  color: var(--ink);
}
.ai-chat__answer :deep(p) {
  margin: 0 0 var(--space-2);
}
.ai-chat__answer :deep(ul) {
  margin: 0 0 var(--space-2);
  padding-left: 1.1rem;
  list-style: disc;
}
.ai-chat__answer :deep(h4) {
  font-weight: 600;
  margin: var(--space-2) 0 var(--space-1);
}
.ai-chat__pages {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  margin-top: var(--space-2);
}
.ai-chat__page {
  cursor: pointer;
}
.ai-chat__page:hover {
  background: var(--wash);
}
.ai-chat__meta {
  margin-top: var(--space-2);
  font-size: 0.72rem;
  color: var(--faint);
}
.ai-chat__form {
  border-top: 1px solid var(--line);
  padding: var(--space-3) var(--space-4) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.ai-chat__input-wrap textarea {
  width: 100%;
  resize: none;
  font: inherit;
  border: 0;
  outline: 0;
  background: transparent;
}
.ai-chat__loading {
  display: flex;
  gap: 4px;
  padding: var(--space-2) 0;
}
.ai-chat__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--mute);
  animation: ai-chat-pulse 1s infinite ease-in-out;
}
.ai-chat__dot:nth-child(2) {
  animation-delay: 0.15s;
}
.ai-chat__dot:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes ai-chat-pulse {
  0%, 100% { opacity: 0.25; }
  50% { opacity: 1; }
}
@media (max-width: 1023px) {
  .ai-chat,
  .ai-chat--wide {
    width: 100%;
    height: 70vh;
  }
}
</style>
